import React from "react";
import { graphql, useStaticQuery, Link } from "gatsby";
import Img from "gatsby-image";
import { OutboundLink } from "gatsby-plugin-google-analytics";

function Events() {
  const {
    motherAndBabe,
    motheringTheBones,
    trackingTheTide,
  } = useStaticQuery(graphql`
    query {
      motherAndBabe: file(relativePath: { eq: "mother-and-babe.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      motheringTheBones: file(relativePath: { eq: "mothering-the-bones.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      trackingTheTide: file(relativePath: { eq: "tracking-the-tide.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 564) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  return (
    <div className="px-8 md:px-16 lg:max-w-screen-lg mx-auto">
      <h1 className="my-8 md:my-12 lg:my-16 font-semibold text-lg md:text-2xl lg:text-3xl text-gray-800 tracking-wider text-center">
        Events
      </h1>

      <nav className="border border-gray-300 rounded-md p-4 my-6">
        <h2 className="font-semibold mb-2">Table of Contents</h2>
        <ul className="list-disc list-inside space-y-1">
          <li>
            <a href="#mothering-the-bones">Mothering the Bones</a>
          </li>
          <li>
            <a href="#tracking-the-tide">Tracking the Tide</a>
          </li>
          <li>
            <a href="#postpartum-bcst">
              In-Home Postpartum BCST for Mother and Babe
            </a>
          </li>
        </ul>
      </nav>

      <div className="mt-16">
        <Img
          className="my-6 md:my-12 mx-4"
          fluid={motheringTheBones.childImageSharp.fluid}
          alt="Mothering the Bones"
        />

        <h2
          id="mothering-the-bones"
          className="text-center mt-12 mb-4 md:mt-12 md:mb-6 lg:mt-16 lg:mb-6 font-semibold text-lg md:text-2xl text-gray-700 tracking-wider"
        >
          Mothering the Bones: An Immersive 3 Day Experience
        </h2>

        <h3 className="text-center mb-6 md:mb-8 lg:mb-8 font-semibold text-lg md:text-xl text-gray-700 tracking-wider">
          September 25 &mdash; 27, 2026
        </h3>

        <p className="my-8">
          <p className="font-semibold">
            Mothering the Bones is about tending the female pelvis in a{" "}
            <span className="italic">radically wholistic</span> way:
          </p>
          Woman to Woman - Body to Body - Bones to Bones.
        </p>

        <p className="my-8">
          <p className="font-semibold">
            Women have tended each other this way across time and culture:
          </p>
          Whether receiving, giving or witnessing Mothering the Bones, everyone
          in the room has a sense of <span className="italic">remembering</span>{" "}
          how ancient and sacred this practice is. The intrinsic need of humans
          for connection and holding to move through big things is met by this
          powerful practice.
        </p>

        <p className="mt-8 mb-2 font-semibold">
          The intension of this gathering:
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>To reweave the threads of communal holding and healing.</li>
          <li>
            To centre human connection, exploring and practicing what it means
            to be human together.
          </li>
        </ul>

        <p className="mt-8 font-semibold">
          The practice of Mothering the Bones involves:
        </p>

        <p>
          Embodiment, attuned presence, skilled touch and nervous system
          tracking. We will learn to feel, communicate and surf the waves of the
          innate healing response that mobilizes when a living body is held this
          way.
        </p>

        <p className="mt-8 font-semibold">
          This seminar is about Apprenticeship:
        </p>

        <p>
          This seminar will be demonstration and practice focused so that you
          learn from the experience itself.{" "}
          <span className="italic">
            You will be held and you will hold others.
          </span>
        </p>

        <p className="my-4">
          Oral and touch transmission will be the main pathway of teaching.
          Presence and practice will be the main pathway of learning and skill
          development.
        </p>

        <p className="my-4">
          It is important to understand that Mothering the Bones is a{" "}
          <span className="italic">lay-person</span>
          or <span className="italic">common folk</span> practice. Therefore
          this seminar is <span className="font-semibold italic">not</span>{" "}
          certificate based nor about a technique to &quot;get&quot; or master.
          This seminar <span className="font-semibold italic">is</span> about
          cultivating skills and the embodied understanding of principles that
          support{" "}
          <span className="italic">positive reparative experiences</span> within
          and between humans.
        </p>

        <p className="mt-8 mb-4 font-semibold">
          During our time together we will:
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>Move and inhabit our bodies</li>
          <li>Practice somatic tracking and attunement</li>
          <li>Do real time repair work</li>
          <li>Witness a live somatic session</li>
          <li>Learn to be with process rather than seek an outcome</li>
          <li>
            Practice ceremony in the ordinary, not as performance, but as{" "}
            <span className="italic">presence</span>
          </li>
          <li>
            Build curiosity for the emergent and steadiness in relationship to
            the unknown
          </li>
        </ul>

        <p className="mt-8 mb-4 font-semibold">
          Topics that could be acknowledged in the space have a relationship to
          the experiences the female pelvis holds:
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>
            Birth Continuum: conception, pregnancy, birth, postpartum
            experiences
          </li>
          <li>
            Gynaecology: female reproductive/pleasure system health, conditions,
            procedures
          </li>
          <li>
            Cycles/Transformation: Menarche, Matrescence, Sage-escence/Menopause
          </li>
          <li>Sexuality: pleasure, the erotic, boundary ruptures, shame</li>
        </ul>

        <p className="mt-8 mb-4 font-semibold">
          This immersive experience may be for you if:
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>
            You&apos;re interested in what it means to bring ceremony into our
            touch transactions - not for the sake of ceremony itself, but in the
            name of presence, allowing for the emergent and shared experience.
          </li>
          <li>
            You&apos;re interested in what it means to remember and centre
            humanness in your interactions with others.
          </li>
          <li>
            You might work with Women/Mothers in the birth continuum and are
            looking for a wholistic practice to support them to process &amp;
            integrate their big experiences
          </li>
          <li>
            You&apos;re interested in exploring what it means to slow down
            enough to allow your body to lead you
          </li>
          <li>
            You&apos;re interested in holding humans rather than doing holds to
            humans
          </li>
          <li>
            You&apos;re interested in cultivating a relationship with your
            sensory intelligence and inner knowing
          </li>
          <li>
            You&apos;re interested in exploring the transformative healing power
            of attuned presence and skilled touch that listens to what the body
            has to share
          </li>
          <li>
            You know that the body is designed to protect and sustain life and
            that there is a way to support the body in alignment with this
            innate intelligence - even if you don’t know how, you know that
            there is a <span className="italic">right</span> way to support
            healing
          </li>
        </ul>

        <p className="mt-8 mb-4 font-semibold">
          This immersive experience may NOT be for you if:
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>
            You are currently navigating a crisis and are unable to bring your
            wise, resourced, adult Self to the seminar
          </li>
          <li>
            Reading this seminar description sounds or feels a little too
            intimate or you don&apos;t feel ready to explore this level of
            vulnerability at this time
          </li>
          <li>
            You&apos;re uncomfortable with inviting exploration and the emergent
            into the learning environment. You prefer slide shows, lecture,
            concrete structure, protocol etc.
          </li>
          <li>
            You are looking for <span className="italic">another</span>{" "}
            certificate of achievement to add to your list
          </li>
        </ul>

        <p className="mt-8 font-semibold">Investment: $825</p>
        <p>Payment plans available</p>

        <p className="mt-8 font-semibold">
          Space is limited to 12 participants
        </p>
        <p className="italic">
          Any Woman that feels the call is welcome to register.
        </p>

        <p className="mt-8 font-semibold">For more info or to register:</p>
        <p>
          Email:{" "}
          <a
            href="mailto:wellspringcranio@gmail.com"
            className="no-underline hover:underline text-blue-500"
          >
            wellspringcranio@gmail.com
          </a>
        </p>
        <p>
          Call/text:{" "}
          <a
            href="tel:+17802162609"
            className="no-underline hover:underline text-blue-500"
          >
            780-216-2609
          </a>
        </p>

        <p className="mt-8 font-semibold">Schedule: 09:30-5:30 each day</p>
        <p>*Online Follow-up Circle:</p>
        <p>Friday, October 9 @ 12:00-2:00</p>

        <p className="mt-8 font-semibold">Location: Rising Moon Studio</p>
        <p>Nature will hold us as we hold each other</p>
        <p>52219 RR231 Sherwood Park, Ab T8B1A7</p>

        <p className="my-4">
          <Link
            to="/appointments#appointment-form"
            className="mt-3 inline-block bg-gray-600 hover:bg-gray-700 md:text-xl text-white font-bold tracking-wide md:tracking-wider py-2 px-4 rounded shadow"
          >
            Register
          </Link>
        </p>

        <p className="mt-8 font-semibold">Led by Angela Dechaine, RCST:</p>

        <p className="">
          Angela is a Mother, INNATE Postpartum Care Certified Practitioner, and
          Biodynamic Craniosacral Therapy Practitioner &amp; Educator. She works
          with humans in a wholistic way that honours our physiological need for
          connection, attunement and touch. She is devoted to practices that
          centre humanness so that we remember what that means within and
          between us.
        </p>

        <p className="mt-8 font-semibold">
          Here’s what Women are saying about their Mothering the Bones
          experience:
        </p>

        <p className="">
          This past weekend, I took a 3-day workshop called &apos;Mothering the
          Bones&apos;, led by Angela Dechaine of Wellspring Craniosacral
          Therapy. This course intrigued me when I read about it because it was
          about &apos;tending the female pelvis&apos; – an area that I had not
          learned much about in my Somatic Experiencing training – and we would
          be taught how to touch and hold women in their pelvic area in a
          supportive and healing manner.
          <br />
          In my previous bodywork trainings, I have never had the sense of
          safety and clarity of purpose that I experienced in this course with
          Angie. It was obvious that she had given much care and intention to
          how she structured our learning to prepare us for the actual touch
          work. Two and a half days of learning about Give/Receive/Take/Allow,
          about negotiation, about creating the conditions for true safety to
          prepare us to touch and be touched. Because of this care and
          preparation, when we got to practice holding and supporting another
          woman&apos;s pelvis, the learning seemed to just fall into place.
          Feeling safe, feeling heard, feeling supported in both our learning
          and in our receiving care from another. Bones and tissues that had
          carried the energy of accidents, of difficult birthing, of caesarean
          sections, of various traumas, had the opportunity to be witnessed and
          to let go.
          <br />
          My pelvis has held injuries from falling on ice, breaking my sacrum,
          torn ligaments from a fall off a horse, in addition to holding deep
          visceral memories of very early childhood trauma. I was so incredibly
          fortunate to be a demo client for Angela and, as she &apos;mothered my
          bones,&apos; I could begin to connect into the sensations in my pelvic
          area in a new way. An area that had always been somewhat numb and
          foggy began to let go of years of &apos;freeze&apos; response and I
          could feel aliveness return to my pelvis and spread down my legs to my
          ankles and feet. It made me wonder if the reason I have so much
          trouble walking now is because the lower part of my body has been
          &apos;disconnected&apos; for most of my life!!
          <br />
          I can see that Mothering the Bones will be a powerful addition to my
          Somatic Experiencing trauma recovery work. But my real learning this
          weekend was about the care and intention that needs to go into
          creating the safe space for this most sacred and vulnerable work.
          <br />
          <span className="font-semibold">Barb Ganske, Reclaim Connection</span>
          <br />
          <span className="font-semibold">
            Somatic Experiencing Practitioner, Eponaquest Instructor, BSc
            (Pharm), MTS (Spirituality)
          </span>
          <br />
          <br />
          Wowwwwwww. I am speechless for how impactful this weekend was. What a
          gift you have for teaching and supporting. The way you educate through
          empowerment is deeply misssing in the &quot;wholistic&quot; world and
          I learned so much! I fall in love with humans who hold their medicine
          with depth &amp; integrity and you do without hesitation, which is
          even more liberating to witness. You are a rate little creature and
          how grateful I am that our paths crossed.
          <br />
          <span className="font-semibold">
            Richel Brousseau, Hallow Bone Collective
          </span>
          <br />
          <span className="font-semibold">Holistic Medicine Practitioner</span>
          <br />
          <br />
          This is the work I feel like my body has been waiting/longing for; the
          language and context that I have been searching for and the space to
          practice while being fully immersed in the medicine itself. I am
          really grateful for the way you teach and how you hold everyone while
          doing so. I went into the weekend with the intention to hold and be
          held - with the wanting to be held a little more than I thought I
          wanted to hold. Holding another woman was soooo informative, as well
          as the support I received from you afterwards. I became very aware of
          how much I have ability to hold and the parts of me that heal when
          holding others this way.
          <br />
          <span className="font-semibold">
            Natasha Dupuis, Natasha Marie Rose
          </span>
          <br />
          <span className="font-semibold">Somatic Healing Practitioner</span>
          <br />
          <br />
          This experience put language to the things I was already intuitively
          sensing. It was a demonstration of how to be with a human, allowing
          for the emergent.
          <br />
          <span className="font-semibold">
            Shari Lange, Parallel Effects Equine
          </span>
          <br />
          <span className="font-semibold">
            Certified Trauma Informed Horse Facilitated Development and Grief
            Guide
          </span>
          <br />
          <br />
          Integrating what I learned at Mothering the Bones into my life and
          practice has been so nourishing. It&apos;s like I changed over that
          weekend, and everything became different in the most excellent way. I
          feel more like myself, and it&apos;s a Self I didn&apos;t really know
          was in there. It feels like a coming back home and a lot of things are
          just more clear. There&apos;s less confusion and muddling around
          things in my life that have been challenging or hard, they seem more
          simple. In my work we go deeper; there is something powerful about
          humans being together this way and the feedback from my clients has
          been fulfilling to my heart.
          <br />
          <span className="font-semibold">Michelle Little, Soul to Sole</span>
          <br />
          <span className="font-semibold">Reflexology and Energy Healing</span>
          <br />
          <br />
          My experience from this weekend makes me emotional because it was so
          special and impactful in the most luscious way. The way you presented
          the material and held everything was so beautiful. It was like the
          things that I have felt with my inner knowing collided with tangible
          principles and practices. These have now trickled into every aspect of
          my life and have led to big and meaningful shifts. I&apos;m receiving
          unbelievable feedback from clients and I&apos;m excited to see what
          will continue to unfold.
          <br />
          <span className="font-semibold">
            Nicole Broadhurst, Co-Owner of Holistic Fertility Group
          </span>
          <br />
          <span className="font-semibold">Doctor of Acupuncture, TCMD</span>
        </p>

        <br />

        <p className="my-4">
          <Link
            to="/appointments#appointment-form"
            className="mt-3 inline-block bg-gray-600 hover:bg-gray-700 md:text-xl text-white font-bold tracking-wide md:tracking-wider py-2 px-4 rounded shadow"
          >
            Register
          </Link>
        </p>

        <hr className="w-4/6 my-16 mx-auto" />

        <Img
          className="my-6 md:my-12 mx-4"
          fluid={trackingTheTide.childImageSharp.fluid}
          alt="Tracking the Tide"
        />

        <h2
          id="tracking-the-tide"
          className="text-center mt-12 mb-4 md:mt-12 md:mb-6 lg:mt-16 lg:mb-6 font-semibold text-lg md:text-2xl text-gray-700 tracking-wider"
        >
          Tracking the Tide
        </h2>

        <h3 className="text-center mb-4 md:mb-4 lg:mb-4 font-semibold text-lg md:text-lg text-gray-700 tracking-wider">
          Exploring Emergent Tidal Expressions
        </h3>

        <h3 className="text-center mb-6 md:mb-8 lg:mb-8 text-lg md:text-lg text-gray-700 tracking-wider">
          A 5 Day Immersive Experience
        </h3>

        <h3 className="text-center font-semibold mb-6 md:mb-8 lg:mb-8 text-lg md:text-lg text-gray-700 tracking-wider">
          October 21-25, 2026
        </h3>

        <p className="mt-8">
          The emphasis of this seminar will be on map building the expressions
          and therapeutic effects of the Tide within living human systems. Map
          building helps us to feel oriented when working within an emergent
          landscape; orientation supports your system to feel safe, curious and
          confident in the face of the unknown.
        </p>

        <p className="my-8">
          The focus of this seminar will be on experiential research through
          hands on sessions and discussion. We will start our map building with
          what we know to be true. Then we will apprentice the Breath of Life
          and living body to clarify and build out our maps of the Tide and
          therapeutic process. This exploratory learning dynamic sets us up to
          actively practice the foundational skills required for working with
          the Tide and making sense of what we are perceiving - even if it is
          novel, unfamiliar or contradicts what we have been taught.
        </p>

        <p className="mt-8 font-semibold">
          This seminar is about Apprenticeship:
        </p>
        <p>
          It will be demonstration and practice focused so that you learn from
          the experience itself. Oral and touch transmission will be the main
          pathway of teaching. Presence and practice will be the main pathway of
          cultivating an embodied understanding of the foundational principles
          and skills required for working with the Tide.
        </p>

        <p className="mt-8 font-semibold">
          This seminar will transform your practice:
        </p>
        <p>
          Shift OUT of having a vague sense of what is happening in session and
          the uncertainty of what to do next or what to communicate to clients.
        </p>

        <p className="my-4">
          Shift INTO tracking the Tide and therapeutic process with clarity,
          respond with more confidence in session and interact with clients in a
          way that centres their somatic experience of the session
        </p>

        <p className="mt-8 font-semibold">Limited to 6 participants:</p>
        <p>
          This is an immersive small group{" "}
          <span className="italic">mentorship</span> seminar where participants
          will work closely with Angela Dechaine and each other to track and map
          the Tide and therapeutic process.{" "}
          <span className="italic">
            The goal is to maximize the personal mentorship opportunities for
            each participant within the group.
          </span>
        </p>

        <p className="mt-8 mb-4 font-semibold">
          Each participant will have the opportunity to experience:
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>
            A <span className="italic">full</span> BCST session from Angela for
            demonstration - learn while you are personally tended by Angela and
            simultaneously witnessed by the group with attuned presence.
          </li>
          <li>
            A <span className="italic">full</span> BCST session where you are in
            the practitioner role and Angela sits in to guide and mentor you in
            tracking the Tide and therapeutic process.
          </li>
        </ul>

        <p className="mt-8 mb-4 font-semibold">Goals of this seminar:</p>

        <ul className="my-2 list-disc ml-8">
          <li>
            Learn foundational principles and practices for tracking the Tide so
            that you feel confident to sit with emergent processes going forward
            with curiosity and excitement for the unknown.
          </li>
          <li>
            Explore the principles of pattern recognition, taking cues from
            nature to support our sense making of how the Tide moves through and
            shifts the rhythmical state of the living body.
          </li>
          <li>
            Map out the different expression of the Tide that we have noticed in
            our practice and what effects they appear to have on the living
            human system together. Explore the emergent expressions of the Tide
            in session to clarify and build out our maps of the Tide and
            therapeutic process.
          </li>
          <li>
            Learn how to create the conditions for the Tide to reveal itself and{" "}
            <span className="italic">discredit</span> the idea that some bodies
            cannot access fluidity or the Tide.
          </li>
          <li>
            Build/refine an embodied connection to the unified fluid field (aka
            fluid body), how and why it emerges and what that means in
            relationship to the healing process and thriving life. Get in touch
            with your innate sea creature motility, embryology and accumulated
            biological wisdom.
          </li>
          <li>
            Map out the natural arc of a therapeutic session (beginning, middle
            and completion), the patterns that cue what phase of the session we
            are in and the transitions between each phase of the session from
            beginning to completion.
          </li>
          <li>
            Map out the shape of the Tide as it moves through the living human
            system as a wave and the effects each part of the wave has on the
            body physiology and the therapeutic process. Map out the common
            pathway the Tide moves through in its inhale/exhale cycle and the
            variations it can take.
          </li>
          <li>
            Map out the therapeutic process into 4 phases and how the Tide
            interacts with the living body in each phase.
          </li>
          <li>
            Explore what inertia is and how the Tide interacts with it in form
            and field. Explore what an inertial fulcrum is, how the practitioner
            can locate it and why it matters.
          </li>
        </ul>

        <p className="mt-8 mb-4 font-semibold">
          This seminar could be for you if:
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>
            You find the Tide mysterious and/or do not yet fully understand its
            role in the therapeutic process. You might be sensing things that
            are hard to name or you feel unsure about what you are sensing. You
            know that there is more to communing with the Breath of Life and
            Tide and wish someone could guide you in relating to the nuances of
            this work in real time.
          </li>
          <li>
            You&apos;re interested in learning in community and the synergistic
            potential of collaboration. The emphasis of this seminar is to
            explore the Tide and map build{" "}
            <span className="font-semibold">together</span>. We will research,
            share, make sense of and celebrate our discoveries as a group.
          </li>
          <li>
            You&apos;re interested in reweaving the threads of communal holding
            and healing. As we behold the Breath of Life within each other and
            ourselves, big healing can unfold. We will practice holding what
            emerges in the healing process collectively, tending ourselves and
            one another with attuned presence. In doing so may we remember what
            is understood across time and culture - that healing is{" "}
            <span className="italic">not</span> meant to be traversed alone.
          </li>
          <li>
            You&apos;re interested in apprenticeship. Apprenticeship is about
            learning over time through presence &amp; practice, relationship,
            wisdom transmission and mentorship. The Breath of Life and living
            body are always teaching us. This is a seminar to actively
            build/refine your relationship with these Intelligent teachers with
            skilled guidance and personalized mentorship from Angela. Oral and
            touch transmission will be the main pathway of teaching. Presence
            and practice will be the main pathway of learning and skill
            development.
          </li>
          <li>
            You&apos;re interested in skill building. In this seminar we will
            lean into our sensory intelligence to track emergent processes.
            Skills like curiosity, orientation, pendulation, reverential data
            collection, pattern recognition, inquiry, contemplation, and
            collaboration will be actively practiced.
          </li>
          <li>
            You&apos;re a Truth seeker. You can sense there is more to this work
            than what you&apos;ve been taught, but find what you sense hard to
            name or maybe it contradicts what you’ve been taught and you are
            hesitant to step outside or challenge the paradigm. Truth is that
            which corresponds to reality - if your senses are perceiving
            something, and it is a repeatable pattern or a phenomenon also
            perceived by others, there is a Truth to{" "}
            <span className="italic">discover</span>.
          </li>
          <li>
            You would like to feel nourished and inspired by this seminar.
            Imagine an exit from the city into the country with quieter
            surroundings and slower pace. A beautiful space to practice in with
            natural light, resting next to body of water and the horizon you can
            see out the windows. Surrounded by Nature that you are welcome to
            explore. Supported to grow with a lot of personalized mentorship.
            Held and tended with attuned presence. Genuine human connection and
            a shared experience of discovery.{" "}
            <span className="italic">
              How do those prompts feel in your system?
            </span>
          </li>
        </ul>

        <p className="mt-8 mb-4 font-semibold">
          This seminar would NOT be for you if:
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>
            You are uncomfortable with inviting exploration and the emergent
            into the learning environment. Rather than experiential learning you
            are looking for top-down learning: slide shows, lecture, concrete
            structures, protocols, information acquisition etc.
          </li>
          <li>
            You do not wish to challenge the paradigm you work from. You are
            uncomfortable with the possibility that this exploration could shift
            how you currently understand and practice BCST.
          </li>
          <li>
            You are not interested in learning and healing in community,
            it&apos;s too vulnerable to practice being human together.
          </li>
          <li>
            Your are currently navigating a crisis and are unable to bring your
            wise, resourced, adult Self to the seminar.
          </li>
        </ul>

        <p className="mt-8 font-semibold">Investment: $1600 +gst</p>
        <p>*Limited to 6 participants</p>
        <p>*Payment plans available</p>

        <p className="mt-8 font-semibold">
          Dates: Wednesday October 21 - Sunday October 25, 2026
        </p>

        <p className="mt-8 font-semibold">Schedule: 09:30-5:30 each day</p>

        <p className="mt-8 font-semibold">
          Location: On the Rocks Cabin @ Pigeon Lake, AB
        </p>
        <p className="italic">Nature will hold us as we hold each other</p>

        <ul className="my-2 list-disc ml-8">
          <li>
            Practice space with natural lighting and a clear view of the water
            and horizon
          </li>
          <li>Lake front access with paddle-boards &amp; paddle-boat</li>
          <li>Hot Tub</li>
          <li>Full Kitchen</li>
        </ul>

        <p className="mt-8">
          Limited onsite accommodation available (3 rooms available total):
        </p>

        <ul className="my-2 list-disc ml-8">
          <li>
            Private queen bedroom - shared bathroom. Single $80/night. Double
            $120/night
          </li>
          <li>
            Private bunkhouse with queen/single bunkbed - private bathroom.
            $100/night. Double $150
          </li>
          <li>
            Private queen bedroom - shared bathroom. Single $80/night. Double
            $120/night
          </li>
        </ul>

        <p className="mt-8 font-semibold">For more info or to register:</p>
        <p>
          Email:{" "}
          <a
            href="mailto:wellspringcranio@gmail.com"
            className="no-underline hover:underline text-blue-500"
          >
            wellspringcranio@gmail.com
          </a>
        </p>
        <p>
          Call/text:{" "}
          <a
            href="tel:+17802162609"
            className="no-underline hover:underline text-blue-500"
          >
            780-216-2609
          </a>
        </p>

        <p className="my-4">
          <Link
            to="/appointments#appointment-form"
            className="mt-3 inline-block bg-gray-600 hover:bg-gray-700 md:text-xl text-white font-bold tracking-wide md:tracking-wider py-2 px-4 rounded shadow"
          >
            Register
          </Link>
        </p>

        <p className="mt-8 font-semibold">Led by Angela Dechaine, RCST:</p>

        <p className="">
          In practice since 2014 and teaching since 2020, Angela is devoted to
          restoring what it means to be human together, especially in the
          healing and educational space. She is interested in, and skilled at,
          supporting positive reparative experiences through genuine connection,
          attunement and touch. Angela is an emergent teacher, consistently
          modelling healing work that is embodied, responsive, non-forceful, and
          always in right relationship.
        </p>

        <p className="mt-8 font-semibold">
          What people are saying about Angela:
        </p>

        <p className="">
          &quot;What a gift you have for teaching and supporting. The way you
          educate through empowerment is deeply missing in the
          &quot;wholistic&quot; world. I learned so much I&apos;m speechless for
          how impactful this was! I fall in love with humans who hold their
          medicine with depth &amp; integrity and you do so with out hesitation,
          which is even more liberating to witness. You are a rare little
          creature and how grateful I am that our paths crossed!&quot;
          <br />
          - Richel
          <br />
          <br />
          &quot;In my previous bodywork trainings, I have never had the sense of
          safety and clarity of purpose that I experienced in this course with
          Angie. It was obvious that she had given much care and intention to
          how she structured our learning to prepare us for the actual touch
          work.&quot;
          <br />
          - Barb
          <br />
          <br />
          &quot;I am so thankful to have had Ang as a teacher. She has this way
          of sharing enough of her own vulnerability that it normalizes the
          vulnerable parts of the learning and healing process. She&apos;s able
          to see the human in us all no matter the situation and has this
          admirable way of bringing down the boundaries between student and
          teacher so that a true exchange can emerge. She is one of the only
          teachers I have felt comfortable letting my guard down around. Such
          safety. Such genuine intent to support my learning process in an
          enriching way. Her knowledge, passion and teaching style is magnetic
          and easy to absorb, and her powerful connection to the work made the
          material she shared so relatable.&quot;
          <br />
          - Corina
          <br />
          <br />
          &quot;I so very much appreciate your connection to the work and the
          way you are able to put words to it. Your why and passion and love for
          BCST and what it can mean for healing really comes through. Your
          capacity to be animated and excited is just as important as your
          capacity to crate a deeply grounded and safe space. I will jump at the
          chance to be involved in any offerings you have&quot;
          <br />
          - Cora
          <br />
          <br />
          &quot;From the first day I met Angela in the classroom I felt listened
          to and truly heard no matter how many questions I asked - she even
          answered them all thoroughly! I look forward to her seminars because
          she presents her teaching material with excitement, embodied
          understanding, and a deep reverence for the human body. With her
          patient support and guidance I have gained confidence in my skill set.
          I hope to have the joy of being in her classroom once again in the
          future.&quot;
          <br />
          - Brenda
          <br />
          <br />
          &quot;Angela is a very gifted BCST practitioner and teacher. We have
          worked together for a few years already, and she has been a tremendous
          help. She has not only supported and guided me through the ups and
          downs that are inherent to every new practitioner, but she definitely
          made a difference when I had difficulties finding my fluid body and
          articulating the felt sense experience. She always employs a useful
          yet simple and clear language to talk about phenomena in and outside
          the body that a new practitioner may find hard to explain and make
          sense of. I am truly grateful to have worked with her one on one and
          to be able to reach out to her to chat on various craniosacral topics
          or about what&apos;s going on in my practice. I definitely recommend
          her to any new or not so new student/practitioner who wants to gain
          clarity and deepen their perceptual skills of primary respiration and
          stillness. Thanks, Angie. I couldn&apos;t have made it so far without
          you!&quot;
          <br />- Marian
        </p>

        <br />

        <p className="my-4">
          <Link
            to="/appointments#appointment-form"
            className="mt-3 inline-block bg-gray-600 hover:bg-gray-700 md:text-xl text-white font-bold tracking-wide md:tracking-wider py-2 px-4 rounded shadow"
          >
            Register
          </Link>
        </p>

        <hr className="w-4/6 my-16 mx-auto" />

        <Img
          className="my-6 md:my-12 mx-4"
          fluid={motherAndBabe.childImageSharp.fluid}
          alt="Mother and Babe"
        />

        <h2
          id="postpartum-bcst"
          className="text-center mt-12 mb-6 md:mt-12 md:mb-8 lg:mt-16 lg:mb-8 font-semibold text-lg md:text-2xl text-gray-700 tracking-wider"
        >
          In-Home Postpartum BCST for Mother and Babe
        </h2>

        <div className="mx-6 md:mx-24 mt-12 md:mb-24">
          <blockquote className="pl-4 font-serif leading-loose text-justify border-l-4 border-gray-900">
            Women are the heart, the foundation of the people. Supporting
            Mothers supports healthy and empowered families, which supports
            thriving communities, which supports all of humanity.
          </blockquote>
          <cite className="block mt-4 text-xs font-bold text-right uppercase">
            - Rachelle Garcia Seliga
          </cite>
        </div>

        <p className="my-4">
          I deeply align with{" "}
          <OutboundLink
            className="no-underline hover:underline text-blue-500"
            href="https://www.innatetraditions.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Innate Traditions
          </OutboundLink>{" "}
          and their primary commitment to maternal health and the revival of
          true postpartum care. In-home postpartum BCST for mums and babes is a
          deeply heartfelt service to support this movement in our culture
          toward centring care on mothers and honouring this profound time of
          the 4th trimester. Please reach out through the{" "}
          <Link
            to="/appointments#appointment-form"
            className="no-underline hover:underline text-blue-500"
          >
            request form
          </Link>{" "}
          if you would like an in-home BCST session or would like to gift one to
          a family expecting or newly postpartum. You can also call/text
          directly{" "}
          <a
            href="tel:+17802162609"
            className="no-underline hover:underline text-blue-500"
          >
            780.216.2609
          </a>
          . I’m always willing to be creative to find a time that suits as soon
          as possible.
        </p>
      </div>
    </div>
  );
}

export default Events;
